import { TFunction } from 'i18next'
import { IHealthAndSafetyField, IHSFieldGroup } from '../../models/HSFields'

export interface IIncidentFieldDef {
  detailFields: IHSFieldGroup[]
  investigationFields: IHSFieldGroup[]
}

export function getIncidentName(
  incidentFieldDef: IIncidentFieldDef
): IHealthAndSafetyField {
  const hsField = incidentFieldDef.detailFields[1].fields.find(
    (f) => f.name === 'name'
  )
  return hsField!
}

export function getIncidentFields(
  dateFormat: string,
  _dateTimeFormat: string,
  t: TFunction
): IIncidentFieldDef {
  return {
    detailFields: [
      {
        label: 'Personal Details',
        fields: [
          {
            name: 'nameOfPerson',
            type: 'STRING',
            label: 'Name Of Affected Person',
            visiField: 'nameOfPersonField',
            helpInfoLabel: 'Name Of Affected Person'
          },
          {
            name: 'address',
            type: 'STRING',
            label: 'Person Address',
            visiField: 'addressField',
            helpInfoLabel: 'Address'
          },
          {
            name: 'email',
            type: 'STRING',
            label: 'Person Email',
            visiField: 'emailField',
            helpInfoLabel: 'Email'
          },
          {
            name: 'contact',
            type: 'STRING',
            label: 'Contact Number/Dept',
            visiField: 'contactNumberField',
            helpInfoLabel: 'Contact Number/Dept'
          },
          {
            name: 'employeeType',
            type: 'SELECT:STR',
            label: 'Person Type',
            visiField: 'employeeTypeField',
            helpInfoLabel: 'Person Type',
            selectionList: 'employeeTypesList',
            otherField: 'employeeTypeOther'
          }
        ]
      },
      {
        label: t('customLabel_incident'),
        fields: [
          {
            name: 'nearMissType',
            type: 'SELECT:STR',
            label: 'Near Miss Type',
            selectionList: 'nearMissTypeOptions',
            showBlankOption: false,
            visiField: 'nearMissTypeField'
          },
          {
            name: 'reporter',
            type: 'SELECT',
            label: 'Reporter',
            selectionList: 'reporterOptions',
            visiField: 'reporterField',
            readonly: true
          },
          {
            name: 'name',
            type: 'STRING',
            label: t('customLabel_incidentName'),
            mandatory: true,
            visiField: 'incidentNameField',
            helpInfoLabel: t('customLabel_incidentName')
          },
          {
            name: 'date',
            type: 'DATE',
            label: 'Date Of',
            mandatory: true,
            visiField: 'dateOfIncidentField',
            helpInfoLabel: 'Date Of',
            dateFormat
          },
          {
            name: 'dateReported',
            type: 'DATE',
            label: 'Date Reported',
            readonly: true,
            visiField: 'dateReportedField',
            helpInfoLabel: 'Date Reported',
            dateFormat
          },
          {
            name: 'severity',
            type: 'SELECT:STR',
            label: 'Severity',
            selectionList: 'severityOptionsList',
            mandatory: true,
            visiField: 'severityField',
            helpInfoLabel: 'Severity'
          },
          {
            name: 'injuryType',
            type: 'SELECT:STR',
            label: t('customLabel_incidentTypeInjury'),
            selectionList: 'injuryTypeOptionsList',
            otherField: 'injuryOther',
            mandatory: true,
            visiField: 'injuryTypeField',
            helpInfoLabel: t('customLabel_incidentTypeInjury')
          },
          {
            name: 'injuryPart',
            type: 'STRING',
            label: 'Injured Part Of Body',
            visiField: 'injuryBodyField',
            helpInfoLabel: 'Injured Part Of Body'
          },
          {
            name: 'hazard',
            type: 'SELECT',
            label: 'Hazard Involved',
            visiField: 'hazardField',
            helpInfoLabel: 'Hazard Involved'
          },
          {
            name: 'damaged',
            type: 'STRING',
            label: 'Property/Material Damaged',
            visiField: 'propertyDamagedField',
            helpInfoLabel: 'Property/Material Damaged'
          },
          {
            name: 'natureOfDamage',
            type: 'STRING',
            label: 'Nature Of Damage',
            visiField: 'damageNatureField',
            helpInfoLabel: 'Nature Of Damage'
          },
          {
            name: 'location',
            type: 'STRING',
            label: 'Location',
            visiField: 'locationField',
            helpInfoLabel: 'Location'
          },
          {
            name: 'desc',
            type: 'TEXT_AREA',
            label: 'Description',
            visiField: 'descField',
            helpInfoLabel: 'Description'
          }
        ]
      },
      {
        label: 'Treatment',
        fields: [
          {
            name: 'treatment',
            type: 'SELECT:STR',
            label: 'Treatment Required',
            selectionList: 'treatmentOptionsList',
            otherField: 'treatmentOther',
            visiField: 'treatmentField',
            helpInfoLabel: 'Treatment Required'
          },
          {
            name: 'nameOfFirstAid',
            type: 'STRING',
            label: 'Provider Details',
            visiField: 'firstAidField',
            helpInfoLabel: 'Provider Details'
          }
        ]
      },
      {
        label: 'Signature',
        fields: [
          {
            name: 'detailSignature64BaseData',
            type: 'SIGNATURE',
            label: 'Signature',
            visiField: 'signatureField'
          }
        ]
      }
    ],
    investigationFields: [
      {
        label: 'Analysis',
        fields: [
          {
            name: 'badCouldBe',
            type: 'SELECT:STR',
            label: 'How Bad Could It Have Been',
            selectionList: 'badCouldBeOptionsList',
            visiField: 'howBadField',
            helpInfoLabel: 'How Bad Could It Have Been'
          },
          {
            name: 'chanceHappenAgain',
            type: 'SELECT:STR',
            label: 'Chance Happen Again',
            selectionList: 'chanceHappenAgainOptionsList',
            visiField: 'chanceHappenField',
            helpInfoLabel: 'Chance Happen Again'
          },
          {
            name: 'analysis',
            type: 'STRING',
            label: 'Cause Of',
            visiField: 'causeOfIncidentField',
            helpInfoLabel: 'Cause Of'
          }
        ]
      },
      {
        label: 'Investigation',
        fields: [
          {
            name: 'investigationRequired',
            type: 'SELECT:STR',
            label: 'Investigation Is Required',
            selectionList: 'yesNoOptions',
            showBlankOption: false,
            visiField: 'invRequiredField',
            helpInfoLabel: 'Investigation Is Required'
          },
          {
            name: 'dateInvestigated',
            type: 'DATE',
            label: 'Date Investigated',
            visiField: 'invDateField',
            helpInfoLabel: 'Date Investigated',
            dateFormat
          },
          {
            name: 'investigatedBy',
            type: 'SELECT:STR',
            label: 'Investigated By',
            showBlankOption: false,
            selectionList: 'investigatedByOptionsList',
            otherField: 'investiagatedByOther',
            visiField: 'invByField',
            helpInfoLabel: 'Investigated By'
          },
          {
            name: 'dolAdvised',
            type: 'SELECT:STR',
            label: 'Worksafe Need To Be Advised',
            selectionList: 'yesNoOptions',
            showBlankOption: false,
            visiField: 'needAdvisedField',
            helpInfoLabel: 'Worksafe Need To Be Advised'
          },
          {
            name: 'dateAdvised',
            type: 'DATE',
            label: 'Date Worksafe Advised',
            visiField: 'advisedDateField',
            helpInfoLabel: 'Date Worksafe Advised',
            dateFormat
          },
          {
            name: 'hazardUpdated',
            type: 'SELECT:STR',
            label: t('customLabel_hazard') + ' Register Updated',
            selectionList: 'yesNoOptions',
            showBlankOption: false,
            visiField: 'hazardUpdatedField',
            helpInfoLabel: t('customLabel_hazard') + ' Register Updated'
          },
          {
            name: 'signOff',
            type: 'SELECT:STR',
            label: 'Sign Off',
            selectionList: 'yesNoOptions',
            showBlankOption: false,
            visiField: 'signOffField',
            helpInfoLabel: 'Sign Off'
          },
          {
            name: 'classification',
            type: 'SELECT:STR',
            label: 'Final Classification',
            selectionList: 'classificationOptionsList',
            visiField: 'finalClassificationField',
            helpInfoLabel: 'Final Classification'
          },
          {
            name: 'furtherAction',
            type: 'TEXT_AREA',
            label: 'Further Action',
            visiField: 'furtherActionField',
            helpInfoLabel: 'Further Action'
          },
          {
            name: 'invDesc',
            type: 'TEXT_AREA',
            label: 'Description and Notes',
            visiField: 'invDescField',
            helpInfoLabel: 'Description and Notes'
          }
        ]
      },
      {
        label: "Investigating Officer's Signature",
        fields: [
          {
            name: 'investigationSignature64BaseData',
            type: 'SIGNATURE',
            label: 'Signature',
            visiField: 'invSignatureField'
          }
        ]
      }
    ]
  }
}
